/**
 * Floating Reset Button
 * A persistent button that allows users to access Quick Resets anytime.
 */

import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw } from "lucide-react";

const FloatingResetButton = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);

  // Hide on immersive experiences + public share pages
  const hideOnRoutes = [
    "/golf/coach",
    "/soulumo/from",
    "/soulumo/coach",
    "/atelier/guide",
    "/gym/guide",
    "/networker/guide",
    "/foundations/guide",
    "/lights/guide",
    "/soulumo/guide",
    "/inner-path/guide",
    '/abundance/living-vision-board',
    '/healing',
    '/journeys',
    '/gift-moment',
    '/share/audio',
    '/gift/',
    '/listen',
    '/living-dream',
    '/atelier',
    '/gift/atelier',
    '/share/alignment',
    '/team-alignment',
    '/rewrite',
    '/golf/reclaim-the-shot',
    '/clearing/reclaim-the-pattern',
    '/abundance/rewrite-scarcity',
    '/presence/return-to-here',
    '/relationships/rewrite-the-armour',
    '/performance/free-under-pressure',
    '/inner-path/deep/',
    '/inner-path/daily/',
    '/inner-path/morning-arrival',
    '/heart-ru',
    '/spine/',
    '/heart/',
  ];
  if (hideOnRoutes.some(r => location.pathname.startsWith(r))) return null;

  const handleClick = () => {
    navigate('/networker/resets');
  };

  return (
    <>
      {/* Floating Button - Below header, right side to avoid account/notification overlap */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200 }}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        onClick={handleClick}
        className="fixed top-24 right-3 z-40 w-9 h-9 rounded-full bg-gradient-to-br from-amber-500/90 to-orange-500/90 shadow-lg shadow-amber-500/30 flex items-center justify-center text-white hover:shadow-xl hover:shadow-amber-500/40 transition-shadow"
        data-testid="floating-reset-btn"
        aria-label="Quick Resets"
      >
        <motion.div
          animate={{ rotate: isHovered ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <RefreshCw size={16} strokeWidth={2.5} />
        </motion.div>
      </motion.button>

      {/* Tooltip on hover */}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="fixed top-[100px] right-[48px] z-40 bg-[#1a1a2e]/90 text-white text-xs px-3 py-2 rounded-lg whitespace-nowrap backdrop-blur-sm"
          >
            Quick Resets
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default FloatingResetButton;
